/**
 * Hooks for producer billing state and admin mutations.
 * 
 * BILLING SYSTEM OVERVIEW:
 * ========================
 * Priority for feature resolution:
 * 1) Global kill-switch (paid_plans_enabled in app_settings)
 *    - When OFF, all Starter/Pro plans are treated as Free
 *    - Free plan always works regardless of kill-switch
 * 2) producer_plan_override (if exists and not expired)
 *    - Admin can set a temporary plan override for testing
 * 3) producer subscription plan (free/starter/pro)
 * 4) plan_features mapping
 * 5) producer_feature_overrides (can toggle individual features)
 * 
 * HOW TO DISABLE PAID PLANS GLOBALLY:
 * - Call admin_set_paid_plans_enabled(false) via RPC
 * - All Starter/Pro producers will temporarily become Free
 * - Set to true to restore paid plans
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

// Types for billing state
export interface BillingFeature {
  key: string;
  label: string;
  enabled: boolean;
}

export interface BillingSubscription {
  plan_id: string;
  status: string;
  current_period_start: string | null;
  current_period_end: string | null;
  external_provider: string | null;
}

export interface BillingOverride {
  plan: string;
  expires_at: string | null;
  note: string | null;
}

// Plan limits by feature key (e.g. { events: 3, products: 5 })
export type PlanLimits = Record<string, number | null>;
export type PlanLimitsMatrix = Record<string, PlanLimits>;

export interface ProducerBillingState {
  producer_id: string;
  paid_plans_enabled: boolean;
  effective_plan: 'free' | 'starter' | 'pro';
  subscription: BillingSubscription | null;
  override: BillingOverride | null;
  credit_balance: number;
  features: BillingFeature[];
  plan_limits: PlanLimitsMatrix;
}

export interface CreditLedgerEntry {
  id: string;
  delta: number;
  balance_after: number;
  transaction_type: string;
  note: string | null;
  created_at: string;
}

// Plan display info
export const PLAN_LABELS: Record<string, { name: string; description: string }> = {
  free: { name: 'Gratis', description: 'Grundläggande närvaro på Farmly' },
  starter: { name: 'Starter', description: 'För mindre gårdar som vill växa' },
  pro: { name: 'Pro', description: 'Full kraft för etablerade värdar' },
};

/**
 * Fetch producer billing state including effective plan, credits, and features.
 */
export function useProducerBillingState(producerId: string | undefined) {
  return useQuery({
    queryKey: ['producer-billing', producerId],
    queryFn: async (): Promise<ProducerBillingState> => {
      if (!producerId) throw new Error('Producer ID required');
      
      const { data, error } = await supabase.rpc('get_producer_billing_state', {
        p_producer_id: producerId,
      });
      
      if (error) throw error;
      return data as unknown as ProducerBillingState;
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });
}

/**
 * Fetch credit ledger entries for a producer.
 */
export function useCreditLedger(producerId: string | undefined, limit = 20) {
  return useQuery({
    queryKey: ['credit-ledger', producerId, limit],
    queryFn: async (): Promise<CreditLedgerEntry[]> => {
      if (!producerId) throw new Error('Producer ID required');
      
      const { data, error } = await supabase.rpc('get_credit_ledger', {
        p_producer_id: producerId,
        p_limit: limit,
      });
      
      if (error) throw error;
      return (data as unknown as CreditLedgerEntry[]) || [];
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });
}

/**
 * Admin: Adjust producer credits.
 */
export function useAdminAdjustCredits() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      producerId, 
      delta, 
      note 
    }: { 
      producerId: string; 
      delta: number; 
      note?: string;
    }) => {
      const { data, error } = await supabase.rpc('admin_adjust_credits', {
        p_producer_id: producerId,
        p_delta: delta,
        p_note: note || null,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { producerId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-billing', producerId] });
      queryClient.invalidateQueries({ queryKey: ['credit-ledger', producerId] });
    },
  });
}

/**
 * Admin: Set plan override for a producer.
 */
export function useAdminSetPlanOverride() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      producerId, 
      overridePlan, 
      expiresAt, 
      note 
    }: { 
      producerId: string; 
      overridePlan: string | null; 
      expiresAt?: string | null;
      note?: string;
    }) => {
      const { data, error } = await supabase.rpc('admin_set_plan_override', {
        p_producer_id: producerId,
        p_override_plan: overridePlan,
        p_expires_at: expiresAt || null,
        p_note: note || null,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { producerId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-billing', producerId] });
    },
  });
}

/**
 * Admin: Set feature override for a producer.
 */
export function useAdminSetFeatureOverride() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      producerId, 
      featureKey, 
      enabled, 
      note 
    }: { 
      producerId: string; 
      featureKey: string; 
      enabled: boolean | null; 
      note?: string;
    }) => {
      const { data, error } = await supabase.rpc('admin_set_feature_override', {
        p_producer_id: producerId,
        p_feature_key: featureKey,
        p_enabled: enabled,
        p_note: note || null,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { producerId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-billing', producerId] });
    },
  });
}

/**
 * Admin: Toggle global paid plans (kill-switch).
 */
export function useAdminSetPaidPlansEnabled() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const { data, error } = await supabase.rpc('admin_set_paid_plans_enabled', {
        p_enabled: enabled,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      // Invalidate all billing queries
      queryClient.invalidateQueries({ queryKey: ['producer-billing'] });
    },
  });
}

/**
 * Admin: Set producer plan directly (without Stripe).
 */
export function useAdminSetProducerPlan() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      producerId, 
      planId 
    }: { 
      producerId: string; 
      planId: 'free' | 'starter' | 'pro';
    }) => {
      const { data, error } = await supabase.rpc('admin_set_producer_plan', {
        p_producer_id: producerId,
        p_plan_id: planId,
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: (_, { producerId }) => {
      queryClient.invalidateQueries({ queryKey: ['producer-billing', producerId] });
    },
  });
}

/**
 * Fetch all subscription plans.
 */
export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');
      
      if (error) throw error;
      return data;
    },
    staleTime: 60_000 * 5, // Cache for 5 minutes
  });
}
